import { createMachine } from 'xstate';

import shared from 'common/bento/shared';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import { AnyMachineEvent } from 'features/Bento/types/Abstract';

import guards, { Guard } from './guards';
import model, { Context } from './model';
import services, { Service } from './services';

export enum State {
  INIT = 'init',

  FINANCIAL_INFO_REVIEW = 'financial-info-review',
  FINANCIAL_INFO_EDIT = 'financial-info-edit',
  SAVE_FINANCIAL_INFO = 'save-financial-info',
  COMPLIANCE = 'compliance',

  // Exit states
  DONE = 'done',
  ABORT = 'abort',
}

const machine = createMachine<Context, AnyMachineEvent>(
  {
    context: model.initialContext,

    id: 'company-info',

    initial: State.INIT,

    states: {
      [State.INIT]: {
        always: [
          {
            cond: Guard.ShouldReviewFinancialInfo,
            target: State.FINANCIAL_INFO_REVIEW,
          },
          {
            cond: Guard.ShouldAnswerFinancialInfo,
            target: State.FINANCIAL_INFO_EDIT,
          },
          {
            target: State.COMPLIANCE,
          },
        ],
      },

      [State.FINANCIAL_INFO_REVIEW]: {
        entry: shared.actions.trackView(),

        on: {
          EDIT_FINANCIAL_INFO: State.FINANCIAL_INFO_EDIT,
          PREV: State.ABORT,
        },
      },

      [State.FINANCIAL_INFO_EDIT]: {
        entry: shared.actions.trackView(),
        on: {
          PREV: [
            {
              cond: Guard.ShouldReviewFinancialInfo,
              target: State.FINANCIAL_INFO_REVIEW,
            },
            {
              target: State.ABORT,
            },
          ],
          SAVE_FINANCIAL_INFO: State.SAVE_FINANCIAL_INFO,
        },
      },

      [State.SAVE_FINANCIAL_INFO]: {
        invoke: {
          onDone: [
            {
              cond: Guard.ShouldAnswerCompanyCompliance,
              target: State.COMPLIANCE,
            },
            {
              cond: Guard.ShouldReviewFinancialInfo,
              target: State.FINANCIAL_INFO_REVIEW,
            },
            {
              target: State.DONE,
            },
          ],
          onError: {
            actions: shared.actions.error(),
            target: State.FINANCIAL_INFO_EDIT,
          },

          src: Service.SaveFinancialInfo,
        },
      },

      [State.COMPLIANCE]: {
        on: {
          NEXT: [
            {
              cond: Guard.ShouldReviewFinancialInfo,
              target: State.FINANCIAL_INFO_REVIEW,
            },
            {
              target: State.DONE,
            },
          ],
          PREV: [
            {
              cond: Guard.ShouldAnswerFinancialInfo,
              target: State.FINANCIAL_INFO_EDIT,
            },
            {
              target: State.ABORT,
            },
          ],
        },
      },

      [State.ABORT]: {
        data: {
          status: BentoModuleDoneStatus.ABORT,
        },
        type: 'final',
      },

      [State.DONE]: {
        data: {
          status: BentoModuleDoneStatus.DONE,
        },
        type: 'final',
      },
    },
  },

  {
    actions: {},
    guards,
    services,
  },
);

export default machine;
